import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

/**
 * The firm's internal users, once, for rendering a user reference as a person.
 *
 * A `*_user_id` column holds an identifier like `org-…-user-jsmith_example_com`. Nothing on a
 * screen should ever print that: the reader wants "Jane Smith". Every page that shows an
 * assignment (client team, parcel assignments, generic detail grids) resolves through this
 * hook so the rule is applied once rather than per card, and so the same people list feeds
 * the pickers that edit those fields.
 *
 * Inactive users are kept in the map on purpose — a record assigned to someone who has since
 * left should still name them — and are excluded from `options`, the list a picker offers.
 * Pickers that read `/api/users-list` directly never see them: the server serves that list
 * active-only unless asked with `include_inactive=1`, which only the name resolvers do.
 */
export interface UserRef {
  id: string;
  email: string;
  role: string;
  first_name: string | null;
  last_name: string | null;
  is_active?: boolean | number | null;
  status?: string | null;
  contact_id?: string | null;
}

export function userDisplayName(u: Pick<UserRef, 'first_name' | 'last_name' | 'email'> | undefined | null): string {
  if (!u) return '—';
  return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || '—';
}

export function isActiveUser(u: Pick<UserRef, 'is_active' | 'status'>): boolean {
  if (u.status) return u.status === 'active';
  return u.is_active === undefined || u.is_active === null ? true : Boolean(u.is_active);
}

/** The people list INCLUDING deactivated accounts. Keyed apart from the plain ['users-list']
 *  the pickers use, which the server serves active-only. */
const EXTERNAL_ROLES = new Set(['client', 'consultant']);

export const USERS_LIST_QUERY_KEY = ['users-list', 'all'] as const;
export const USERS_LIST_ALL_URL = '/api/users-list?include_inactive=1';

export function useUserNames() {
  const query = useQuery<UserRef[]>({
    queryKey: USERS_LIST_QUERY_KEY,
    queryFn: ({ signal }) => api.get<UserRef[]>(USERS_LIST_ALL_URL, signal),
    staleTime: 5 * 60_000,
  });
  const byId = useMemo(() => {
    const m = new Map<string, UserRef>();
    for (const u of query.data ?? []) m.set(u.id, u);
    return m;
  }, [query.data]);
  /** Full name for a user id; an unknown id renders as a dash, never as the id. */
  const nameOf = (id: unknown): string => {
    if (!id) return '—';
    const u = byId.get(String(id));
    return u ? userDisplayName(u) : '—';
  };
  /** Internal, active users as picker options, sorted by name. A portal user (client) and an
   *  external consultant both hold logins but are not the firm's people; they are offered
   *  where they belong, under Client Contacts. */
  const options = useMemo(
    () => (query.data ?? [])
      .filter((u) => !EXTERNAL_ROLES.has(u.role) && isActiveUser(u))
      .map((u) => ({ value: u.id, label: userDisplayName(u) }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [query.data],
  );
  return { ...query, byId, nameOf, options };
}

/** A column that references a user: the `_user_id` suffix or a FK to `users`. */
export function isUserReference(fieldName: string, fkTable?: string | null): boolean {
  return fkTable === 'users' || /_user_id$/.test(fieldName) || fieldName === 'updated_by' || fieldName === 'created_by';
}
